import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "results", "empty", "selections"]
  static values = { url: String, fieldName: String, assignmentLabel: { type: String, default: "subtask" } }

  connect() {
    this.onDocumentClick = this.handleDocumentClick.bind(this)
    document.addEventListener("click", this.onDocumentClick)
  }

  disconnect() {
    document.removeEventListener("click", this.onDocumentClick)
    window.clearTimeout(this.debounceTimer)
    this.abortController?.abort()
  }

  open() {
    this.search()
  }

  search() {
    this.resultsTarget.classList.add("hidden")
    this.emptyTarget.classList.add("hidden")
    window.clearTimeout(this.debounceTimer)
    this.debounceTimer = window.setTimeout(() => this.fetchUsers(), 180)
  }

  async fetchUsers() {
    if (!this.hasUrlValue) return

    this.abortController?.abort()
    this.abortController = new AbortController()
    const url = new URL(this.urlValue, window.location.origin)
    url.searchParams.set("q", this.inputTarget.value.trim())

    try {
      const response = await fetch(url, { headers: { "Accept": "application/json" }, credentials: "same-origin", signal: this.abortController.signal })
      if (!response.ok) throw new Error("Could not load team members")
      const payload = await response.json()
      this.render(payload.users || [])
    } catch (error) {
      if (error.name !== "AbortError") this.render([])
    }
  }

  render(users) {
    const selectedIds = this.selectedIds()
    const availableUsers = users.filter((user) => !selectedIds.has(user.id))
    this.resultsTarget.replaceChildren()
    const hasQuery = this.inputTarget.value.trim().length > 0
    this.resultsTarget.classList.toggle("hidden", availableUsers.length === 0)
    this.emptyTarget.classList.toggle("hidden", availableUsers.length > 0 || !hasQuery)

    availableUsers.forEach((user) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
      button.append(this.avatarFor(user), this.copyFor(user))
      button.addEventListener("click", () => this.select(user))
      this.resultsTarget.append(button)
    })
  }

  select(user) {
    if (this.selectedIds().has(user.id)) return
    this.selectionsTarget.append(this.selectionFor(user))
    this.inputTarget.value = ""
    this.inputTarget.placeholder = "Add another…"
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.close()
  }

  remove(event) {
    event.currentTarget.closest("[data-assignee-selection]")?.remove()
    this.inputTarget.placeholder = this.selectedIds().size > 0 ? "Add another…" : "Anyone · add people…"
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.close()
  }

  selectedIds() {
    return new Set(Array.from(this.selectionsTarget.querySelectorAll("[data-user-id]")).map((element) => element.dataset.userId))
  }

  selectionFor(user) {
    const chip = document.createElement("span")
    chip.dataset.assigneeSelection = ""
    chip.dataset.userId = user.id
    chip.className = "inline-flex min-w-0 max-w-[8rem] items-center gap-1 rounded-full bg-purple-100 dark:bg-purple-500/15 py-0.5 pl-0.5 pr-1 text-[9px] font-medium text-purple-700 dark:text-purple-300"

    const hidden = document.createElement("input")
    hidden.type = "hidden"
    hidden.name = this.fieldNameValue
    hidden.value = user.id

    const name = document.createElement("span")
    name.className = "truncate"
    name.textContent = user.name

    const remove = document.createElement("button")
    remove.type = "button"
    remove.textContent = "×"
    remove.ariaLabel = `Remove ${user.name} from ${this.assignmentLabelValue}`
    remove.className = "flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full hover:bg-purple-200 dark:hover:bg-purple-500/30"
    remove.dataset.action = "assignee-picker#remove"

    chip.append(hidden, this.avatarFor(user), name, remove)
    return chip
  }

  avatarFor(user) {
    const avatar = document.createElement("span")
    avatar.className = "inline-flex h-5 w-5 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-purple-200 dark:bg-purple-500/25 text-[8px] font-bold"
    if (user.avatar_url) {
      const image = document.createElement("img")
      image.src = user.avatar_url
      image.alt = ""
      image.className = "h-full w-full object-cover"
      avatar.append(image)
    } else {
      avatar.textContent = (user.name || "?").charAt(0).toUpperCase()
    }
    return avatar
  }

  copyFor(user) {
    const copy = document.createElement("span")
    copy.className = "min-w-0 flex-1"
    const name = document.createElement("span")
    name.className = "block truncate text-xs font-medium text-slate-700 dark:text-slate-200"
    name.textContent = user.name
    const email = document.createElement("span")
    email.className = "block truncate text-[10px] text-slate-400"
    email.textContent = user.email
    copy.append(name, email)
    return copy
  }

  handleDocumentClick(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  close() {
    this.resultsTarget.classList.add("hidden")
    this.emptyTarget.classList.add("hidden")
  }
}
